import { call, put, takeEvery } from "redux-saga/effects";

import * as apis from "../../../network/apis/companyPortalTimesheet";
import { showErrorMsg, showSuccessMsg } from "../../../utils/Helpers";
import * as actions from "./actions";
import * as types from "./types";

const extractListMeta = (response, fallbackCount = 0) => {
  const headers = response?.headers ?? {};

  return {
    currentPage: headers["current-page"] ? +headers["current-page"] : 1,
    count: headers["total-count"] ? +headers["total-count"] : fallbackCount,
    pages: headers["total-pages"] ? +headers["total-pages"] : 1,
  };
};

const buildWorkingSlotsRefetchParams = (payload) => {
  if (payload.refetchParams) {
    return payload.refetchParams;
  }

  return {
    page: 1,
    items: 10,
    event_id: payload.data?.event_id ?? payload.event_id ?? payload.eventId,
    user_id: payload.data?.user_id ?? payload.user_id ?? payload.userId,
    order_by_check_in_at: "desc",
  };
};

const buildTrainingSlotsRefetchParams = (payload) => {
  if (payload.refetchParams) {
    return payload.refetchParams;
  }

  return {
    page: -1,
    event_id: payload.data?.event_id ?? payload.event_id ?? payload.eventId,
    user_id: payload.data?.user_id ?? payload.user_id ?? payload.userId,
  };
};

function* getCompanyWorkingLogsSaga({ payload }) {
  try {
    const response = yield call(apis.getCompanyPortalWorkingLogs, payload);
    const meta = extractListMeta(response);

    yield put(
      actions.getCompanyWorkingLogsResponse({
        list: response?.data?.data ?? [],
        meta,
      }),
    );

    if (+meta.currentPage > +meta.pages && meta.pages > 0) {
      yield put(
        actions.getCompanyWorkingLogsRequest({
          ...payload,
          page: meta.pages,
        }),
      );
    }
  } catch (error) {
    if (error?.response) {
      yield call(showErrorMsg, error.response?.data?.message);
    }
  }
}

function* getCompanyWorkingSlotsSaga({ payload }) {
  try {
    const response = yield call(apis.getCompanyPortalWorkingSlots, payload);
    const data = response?.data?.data ?? [];
    const meta = extractListMeta(response, data.length);

    yield put(
      actions.getCompanyWorkingSlotsResponse({
        list: data,
        meta,
      }),
    );

    if (+meta.currentPage > +meta.pages && meta.pages > 0) {
      yield put(
        actions.getCompanyWorkingSlotsRequest({
          ...payload,
          page: meta.pages,
        }),
      );
    }
  } catch (error) {
    if (error?.response) {
      yield call(showErrorMsg, error.response?.data?.message);
    }
  }
}

function* addCompanyWorkingSlotSaga({ payload }) {
  const { onSuccess, onError, ...requestPayload } = payload;

  try {
    yield call(apis.addCompanyPortalWorkingSlot, requestPayload);
    yield call(showSuccessMsg, "addedSuccessfully");
    yield put(
      actions.getCompanyWorkingSlotsRequest(buildWorkingSlotsRefetchParams(requestPayload)),
    );
    if (requestPayload.refetchWorkingLogs) {
      yield put(actions.getCompanyWorkingLogsRequest(requestPayload.refetchWorkingLogs));
    }
    if (typeof onSuccess === "function") {
      onSuccess();
    }
  } catch (error) {
    if (typeof onError === "function") {
      onError(error);
    }
    if (error?.response) {
      yield call(showErrorMsg, error.response?.data?.message);
    }
  }
}

function* editCompanyWorkingSlotSaga({ payload }) {
  const { onSuccess, onError, ...requestPayload } = payload;

  try {
    yield call(apis.editCompanyPortalWorkingSlot, requestPayload);
    yield call(showSuccessMsg, "updatedSuccessfully");
    yield put(
      actions.getCompanyWorkingSlotsRequest(buildWorkingSlotsRefetchParams(requestPayload)),
    );
    if (requestPayload.refetchWorkingLogs) {
      yield put(actions.getCompanyWorkingLogsRequest(requestPayload.refetchWorkingLogs));
    }
    if (typeof onSuccess === "function") {
      onSuccess();
    }
  } catch (error) {
    if (typeof onError === "function") {
      onError(error);
    }
    if (error?.response) {
      yield call(showErrorMsg, error.response?.data?.message);
    }
  }
}

function* deleteCompanyWorkingSlotSaga({ payload }) {
  try {
    yield call(apis.deleteCompanyPortalWorkingSlot, payload);
    yield call(showSuccessMsg, "deletedSuccessfully");
    yield put(
      actions.getCompanyWorkingSlotsRequest(buildWorkingSlotsRefetchParams(payload)),
    );
    if (payload.refetchWorkingLogs) {
      yield put(actions.getCompanyWorkingLogsRequest(payload.refetchWorkingLogs));
    }
  } catch (error) {
    if (error?.response) {
      yield call(showErrorMsg, error.response?.data?.message);
    }
  }
}

function* getCompanyTrainingWorkingSlotsSaga({ payload }) {
  try {
    const response = yield call(apis.getCompanyPortalTrainingWorkingSlots, payload);
    const data = response?.data?.data ?? [];
    const meta = extractListMeta(response, data.length);

    yield put(
      actions.getCompanyTrainingWorkingSlotsResponse({
        list: data,
        meta,
      }),
    );

    if (+meta.currentPage > +meta.pages && meta.pages > 0) {
      yield put(
        actions.getCompanyTrainingWorkingSlotsRequest({
          ...payload,
          page: meta.pages,
        }),
      );
    }
  } catch (error) {
    if (error?.response) {
      yield call(showErrorMsg, error.response?.data?.message);
    }
  }
}

function* addCompanyTrainingWorkingSlotSaga({ payload }) {
  const { onSuccess, onError, ...requestPayload } = payload;

  try {
    yield call(apis.addCompanyPortalTrainingWorkingSlot, requestPayload);
    yield call(showSuccessMsg, "addedSuccessfully");
    yield put(
      actions.getCompanyTrainingWorkingSlotsRequest(
        buildTrainingSlotsRefetchParams(requestPayload),
      ),
    );
    if (requestPayload.refetchWorkingLogs) {
      yield put(actions.getCompanyWorkingLogsRequest(requestPayload.refetchWorkingLogs));
    }
    if (typeof onSuccess === "function") {
      onSuccess();
    }
  } catch (error) {
    if (typeof onError === "function") {
      onError(error);
    }
    if (error?.response) {
      yield call(showErrorMsg, error.response?.data?.message);
    }
  }
}

function* editCompanyTrainingWorkingSlotSaga({ payload }) {
  const { onSuccess, onError, ...requestPayload } = payload;

  try {
    yield call(apis.editCompanyPortalTrainingWorkingSlot, requestPayload);
    yield call(showSuccessMsg, "updatedSuccessfully");
    yield put(
      actions.getCompanyTrainingWorkingSlotsRequest(
        buildTrainingSlotsRefetchParams(requestPayload),
      ),
    );
    if (requestPayload.refetchWorkingLogs) {
      yield put(actions.getCompanyWorkingLogsRequest(requestPayload.refetchWorkingLogs));
    }
    if (typeof onSuccess === "function") {
      onSuccess();
    }
  } catch (error) {
    if (typeof onError === "function") {
      onError(error);
    }
    if (error?.response) {
      yield call(showErrorMsg, error.response?.data?.message);
    }
  }
}

function* deleteCompanyTrainingWorkingSlotSaga({ payload }) {
  try {
    yield call(apis.deleteCompanyPortalTrainingWorkingSlot, payload);
    yield call(showSuccessMsg, "deletedSuccessfully");
    yield put(
      actions.getCompanyTrainingWorkingSlotsRequest(
        buildTrainingSlotsRefetchParams(payload),
      ),
    );
    if (payload.refetchWorkingLogs) {
      yield put(actions.getCompanyWorkingLogsRequest(payload.refetchWorkingLogs));
    }
  } catch (error) {
    if (error?.response) {
      yield call(showErrorMsg, error.response?.data?.message);
    }
  }
}

export default function* companyEventTimesheetSagas() {
  yield takeEvery(types.GET_COMPANY_WORKING_LOGS_REQUEST, getCompanyWorkingLogsSaga);
  yield takeEvery(types.GET_COMPANY_WORKING_SLOTS_REQUEST, getCompanyWorkingSlotsSaga);
  yield takeEvery(types.ADD_COMPANY_WORKING_SLOT, addCompanyWorkingSlotSaga);
  yield takeEvery(types.EDIT_COMPANY_WORKING_SLOT_REQUEST, editCompanyWorkingSlotSaga);
  yield takeEvery(types.DELETE_COMPANY_WORKING_SLOT_REQUEST, deleteCompanyWorkingSlotSaga);
  yield takeEvery(
    types.GET_COMPANY_TRAINING_WORKING_SLOTS_REQUEST,
    getCompanyTrainingWorkingSlotsSaga,
  );
  yield takeEvery(types.ADD_COMPANY_TRAINING_WORKING_SLOT, addCompanyTrainingWorkingSlotSaga);
  yield takeEvery(
    types.EDIT_COMPANY_TRAINING_WORKING_SLOT_REQUEST,
    editCompanyTrainingWorkingSlotSaga,
  );
  yield takeEvery(
    types.DELETE_COMPANY_TRAINING_WORKING_SLOT_REQUEST,
    deleteCompanyTrainingWorkingSlotSaga,
  );
}
